import { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Search, Menu, Plus, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import WalletButton from '@/components/wallet/WalletButton';
import { useSearch } from '@/hooks/useSearch';
import { SearchDropdown } from '@/components/search/SearchDropdown';
import { NotificationDropdown } from '@/components/notifications/NotificationDropdown';
import { useIsMobile } from '@/hooks/use-mobile';
import { trackClick } from '@/lib/analytics';
import { MobileDrawer } from '@/components/mobile/MobileDrawer';
import { useRecentSearches } from '@/hooks/useRecentSearches';

const TopBar = () => {
  const [searchFocused, setSearchFocused] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const isMobile = useIsMobile();

  const { query, setQuery, results, isLoading, error } = useSearch();
  const { addSearch } = useRecentSearches();

  // Global keyboard listeners: "/" and ⌘K to focus inline search
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      // ⌘K / Ctrl+K — open inline dropdown
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setShowDropdown(true);
        return;
      }

      // "/" — only when not already in an input/textarea
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      if (e.key === '/') {
        e.preventDefault();
        inputRef.current?.focus();
        setShowDropdown(true);
      }
    };

    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Show dropdown when there's a query
  useEffect(() => {
    if (query.length >= 2) {
      setShowDropdown(true);
    }
  }, [query]);

  const handleSearchFocus = () => {
    setSearchFocused(true);
    setShowDropdown(true); // Always show on focus
  };

  const handleSearchBlur = () => {
    setSearchFocused(false);
    // Don't close dropdown immediately to allow clicking on results
  };

  const handleCloseDropdown = () => {
    setShowDropdown(false);
    setQuery('');
    inputRef.current?.blur();
  };

  const handleSearchSelect = useCallback((q: string) => {
    setQuery(q);
    setShowDropdown(true);
    inputRef.current?.focus();
  }, [setQuery]);

  // Track search on Enter key
  useEffect(() => {
    const handleEnterSearch = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && query.trim().length >= 2 && showDropdown) {
        addSearch(query.trim());
      }
    };
    window.addEventListener('keydown', handleEnterSearch);
    return () => window.removeEventListener('keydown', handleEnterSearch);
  }, [query, showDropdown, addSearch]);

  return (
    <header className={cn(
      "fixed right-0 h-14 bg-background border-b border-border z-40 flex items-center",
      isMobile ? "left-0 px-3 gap-2" : "left-52 px-4 gap-4"
    )}
    style={{
      top: 0,
      paddingTop: isMobile ? 'env(safe-area-inset-top, 0px)' : undefined,
    }}
    >
      {/* Mobile: Logo + Hamburger Menu */}
      {isMobile && (
        <>
          <Link to="/" className="flex-shrink-0 flex items-center justify-center h-full min-w-[44px] mr-1">
            <img
              src="/dextoro-icon.png"
              alt="dextoro"
              className="h-6 w-auto"
            />
          </Link>
          <Button variant="outline" size="icon-lg" className="h-11 w-11" onClick={() => setDrawerOpen(!drawerOpen)} aria-label="Toggle menu">
            {drawerOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
          <MobileDrawer open={drawerOpen} onOpenChange={setDrawerOpen} />
        </>
      )}

      {/* Search Bar */}
      <div className={cn("flex-1 relative", isMobile ? "max-w-[200px]" : "max-w-md mx-auto")} ref={searchContainerRef}>
        <div className={cn(
          'relative transition-all duration-200',
          searchFocused && !isMobile && 'scale-105'
        )}>
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            ref={inputRef}
            placeholder={isMobile ? "Search..." : "Search coins, creators, lists..."}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className={cn(
              "pl-10 bg-secondary/50 border-border focus:border-primary focus:ring-1 focus:ring-primary",
              isMobile ? "pr-4 h-11" : "pr-12"
            )}
            onFocus={handleSearchFocus}
            onBlur={handleSearchBlur}
          />

          {/* ⌘K shortcut badge - Hidden on mobile and during search interactions */}
          {!isMobile && !searchFocused && !showDropdown && (
            <div className="absolute right-3 inset-y-0 flex items-center">
              <kbd className="px-1.5 py-0.5 text-[10px] font-medium text-[#888] bg-[#1A1A1A] border border-[#1A1A1A] rounded leading-none">
                {/Mac|iPod|iPhone|iPad/.test(navigator.userAgent) ? '⌘K' : 'Ctrl+K'}
              </kbd>
            </div>
          )}

          {/* Search Results Dropdown */}
          {showDropdown && (
            <SearchDropdown
              results={results}
              isLoading={isLoading}
              error={error}
              query={query}
              onClose={handleCloseDropdown}
              onSearchSelect={handleSearchSelect}
            />
          )}
        </div>
      </div>

      {/* Right Actions */}
      <div className="flex items-center gap-2">
        {/* How It Works Link - Desktop only */}
        {!isMobile && (
          <Link
            to="/how-it-works"
            className="text-sm text-[#888888] hover:text-white transition-colors mr-4"
            onClick={() => trackClick('how_it_works_clicked', { device: 'desktop' })}
          >
            [how it works]
          </Link>
        )}

        {/* Create Button - Desktop only */}
        {!isMobile && (
          <Link
            to="/create"
            className="no-min-touch inline-flex items-center justify-center gap-2 h-9 px-4 rounded-lg text-sm font-bold bg-[#CCFF00] text-black hover:bg-[#CCFF00]/90 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Create
          </Link>
        )}

        {/* Notifications Dropdown */}
        <NotificationDropdown />

        {/* Wallet Button */}
        <WalletButton />
      </div>

      {/* Command Palette removed - ⌘K now opens inline search dropdown */}
    </header>
  );
};

export default TopBar;